// React
import { memo, useCallback, useEffect, useState } from "react";

// Helpers
import { downloadTextFile } from "helpers";

// UI
import { Button } from "ui/components/Button/Button";
import { ButtonMenu } from "ui/components/Button/ButtonMenu";
import ButtonMenuItem from "ui/components/Button/ButtonMenu/ButtonMenuItem";
import { Loader } from "ui/components/Loader";

import { InvoiceXmlPreviewProps } from "./types";

/**
 * eRačun (UBL 2.1 / EN 16931) — točan dokument koji je razmijenjen s Poreznom upravom.
 * Dohvat se prosljeđuje izvana jer izlazni i ulazni računi imaju svoje endpointe.
 */
const InvoiceXmlPreview: React.FC<InvoiceXmlPreviewProps> = (props) => {
  const {
    invoiceId,
    invoiceNumber,
    fetchXml,
    title = "eRačun (XML)",
    description = "UBL 2.1 · EN 16931 — dokument predan Poreznoj upravi",
    fileNamePrefix = "eracun",
    errorMessage = "XML nije moguće dohvatiti. Provjerite podatke računa pa pokušajte ponovno.",
  } = props;

  const [xml, setXml] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  // Stavka izbornika nestaje s ekrana čim se klikne, pa potvrda kopiranja ide uz naslov.
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const loadXml = useCallback(async () => {
    if (!invoiceId) return;

    setIsLoading(true);
    setHasError(false);

    try {
      setXml(await fetchXml(invoiceId));
    } catch {
      setXml("");
      setHasError(true);
    }

    setIsLoading(false);
  }, [invoiceId, fetchXml]);

  useEffect(() => {
    loadXml();
  }, [loadXml]);

  const handleDownloadClick = useCallback(() => {
    if (!xml) return;

    const name = (invoiceNumber ?? invoiceId).replace(/\//g, "-");

    downloadTextFile(xml, `${fileNamePrefix}-${name}.xml`, "application/xml");
  }, [xml, invoiceNumber, invoiceId, fileNamePrefix]);

  const handleCopyClick = useCallback(async () => {
    if (!xml) return;

    try {
      await navigator.clipboard.writeText(xml);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error("Greška prilikom kopiranja XML-a:", err);
    }
  }, [xml]);

  return (
    <div className="relative">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-md font-semibold text-slate-900">{title}</span>
            {isCopied && (
              <span className="text-xs font-medium text-green-600">Kopirano</span>
            )}
          </div>
          <div className="text-sm text-slate-500">{description}</div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="outlined"
            size="small"
            startIcon="/media/icons/refresh.svg"
            onClick={loadXml}
          >
            Osvježi
          </Button>
          <ButtonMenu endIcon="/media/icons/more-dots.svg" variant="text">
            <ButtonMenuItem
              startIcon="/media/icons/download.svg"
              size="full"
              disabled={!xml}
              onClick={handleDownloadClick}
            >
              Preuzmi XML
            </ButtonMenuItem>
            <ButtonMenuItem
              startIcon="/media/icons/copy.svg"
              size="full"
              disabled={!xml}
              onClick={handleCopyClick}
            >
              Kopiraj XML
            </ButtonMenuItem>
          </ButtonMenu>
        </div>
      </div>

      {hasError && !isLoading ? (
        <div className="border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-600">
          {errorMessage}
        </div>
      ) : (
        // Visina prati prozor umjesto fiksnih 600px — kartica računa je modal koji i sam
        // skrola, pa se oduzima ono što iznad zauzimaju zaglavlje, tabovi i naslov.
        <pre className="text-xs font-mono whitespace-pre overflow-auto h-[calc(100vh-320px)] min-h-[320px] bg-slate-50 border border-slate-200 p-4 text-slate-800">
          {xml}
        </pre>
      )}

      <Loader isLoading={isLoading} overlay />
    </div>
  );
};

export default memo(InvoiceXmlPreview);
